import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Info, AlertCircle, Lock, Pencil } from 'lucide-react';

import styles from './PhoneVerificationPage.module.scss';
import { Button } from '../../components/common/dynamic-form/base/Button/Button';
import { getLoanApplicationFormParameters, verifyPhoneNumber } from '../../actions/services';
import { useAppDispatch } from '../../store/hooks';
import {
    incrementLoadingCount,
    decrementLoadingCount,
    showMessage,
    hideMessage,
} from '../../store/globalSlice';
import logo from '../../assets/Icons/yemeksepeti-logo-vendor.png';
import backArrow from '../../assets/Icons/arrow-left.svg';

const DIGIT_COUNT = 5;

interface LocationState {
    offerId?: string;
    identityNumber?: string;
    phoneNumber?: string;
    maskedPhoneNumber?: string; // Orijinal API'den gelen maskeli numara (+90535*****43)
}

const PhoneVerificationPage: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { offerId, identityNumber, phoneNumber, maskedPhoneNumber } =
        (location.state as LocationState) || {};

    // Ekranda gösterilecek ve prefix/suffix hesaplamalarında kullanılacak orijinal maskeli numara.
    // maskedPhoneNumber varsa onu, yoksa phoneNumber'ı kullan.
    const canonicalPhone = maskedPhoneNumber || phoneNumber || '';

    // Redirect to identity-verification if required state is missing (e.g. page refresh)
    useEffect(() => {
        if (!offerId || !identityNumber || !canonicalPhone) {
            console.error('Phone verification: required state missing, redirecting to identity-verification');
            history.replace('/identity-verification');
        }
    }, [offerId, identityNumber, canonicalPhone, history]);

    // Prefix/suffix hesaplaması her zaman orijinal maskeli numara üzerinden yapılır.
    // Bu sayede verifyPhoneNumber isteği de orijinal formata uygun gider.
    let phonePrefix = '';
    let phoneSuffix = '';
    const displayPhoneNumber = canonicalPhone;

    if (canonicalPhone) {
        const firstStarIdx = canonicalPhone.indexOf('*');
        const lastStarIdx = canonicalPhone.lastIndexOf('*');

        if (firstStarIdx !== -1 && lastStarIdx !== -1) {
            // Maskeli format: +90535*****43 → prefix=+90535, suffix=43
            phonePrefix = canonicalPhone.substring(0, firstStarIdx);
            phoneSuffix = canonicalPhone.substring(lastStarIdx + 1);
        } else {
            // Fallback: yıldız yoksa son 7 haneyi koru (kullanıcı 5 hane girecek)
            phonePrefix = canonicalPhone.substring(0, Math.max(0, canonicalPhone.length - 7));
            phoneSuffix = canonicalPhone.substring(Math.max(0, canonicalPhone.length - 2));
        }
    }

    // --- Digit input state ---
    const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill(''));
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const allDigitsFilled = digits.every((d) => d !== '');

    // Clear error when user modifies input
    const updateDigit = useCallback(
        (index: number, value: string) => {
            setDigits((prev) => {
                const next = [...prev];
                next[index] = value;
                return next;
            });
            if (error) setError(null);
        },
        [error]
    );

    // --- Handlers ---

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const raw = e.target.value.replace(/\D/g, ''); // only digits
        if (!raw) return;

        const char = raw.slice(-1);
        updateDigit(index, char);

        // Auto-advance to next input
        if (index < DIGIT_COUNT - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            if (digits[index]) {
                updateDigit(index, '');
            } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
                updateDigit(index - 1, '');
            }
            e.preventDefault();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData
            .getData('text')
            .replace(/\D/g, '') // strip non-digits
            .slice(0, DIGIT_COUNT);

        if (!pasted) return;

        const newDigits = [...digits];
        for (let i = 0; i < pasted.length && i < DIGIT_COUNT; i++) {
            newDigits[i] = pasted[i];
        }
        setDigits(newDigits);
        if (error) setError(null);

        // Focus the next empty input or the last one
        const nextEmpty = newDigits.findIndex((d) => d === '');
        const focusIdx = nextEmpty >= 0 ? nextEmpty : DIGIT_COUNT - 1;
        inputRefs.current[focusIdx]?.focus();
    };

    // Prevent non-digit input via beforeinput for extra safety
    const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
        const inputEvent = e.nativeEvent as InputEvent;
        if (inputEvent.data && /\D/.test(inputEvent.data)) {
            e.preventDefault();
        }
    };

    // --- Submit ---

    const handleSubmit = async () => {
        if (!allDigitsFilled || isSubmitting) return;
        if (!identityNumber || !offerId) return;

        setIsSubmitting(true);
        dispatch(incrementLoadingCount());
        const fullPhoneNumber = phonePrefix + digits.join('') + phoneSuffix;

        try {
            const verifyResponse = await verifyPhoneNumber({
                identityNumber,
                offerId,
                phoneNumber: fullPhoneNumber
            });

            if (verifyResponse.code === '00') {
                dispatch(showMessage({
                    title: "Lütfen Bekleyiniz",
                    text: "Bilgileriniz kontrol ediliyor...",
                }));

                dispatch(hideMessage());
                // maskedPhoneNumber'ı ayrı bir parametre olarak taşıyoruz ki
                // geri butonuna basıldığında orijinal maskeli numara kaybolmasın.
                history.push(
                    `/loan-application?offerId=${offerId}&identityNumber=${identityNumber}&phoneNumber=${encodeURIComponent(fullPhoneNumber)}&maskedPhone=${encodeURIComponent(canonicalPhone)}`
                );
            } else {
                dispatch(showMessage({
                    title: "Hata",
                    text: verifyResponse.message || 'Girdiğiniz telefon numarası kayıtlı numara ile eşleşmedi. Lütfen tekrar deneyiniz.',
                    btnPrimaryText: "Tamam",
                    btnPrimaryCallback: () => dispatch(hideMessage())
                }));
            }
        } catch (err: any) {
            if (err?.response?.status === 429) {
                dispatch(hideMessage());
                return;
            }

            const errorData = err?.response?.data;
            let errorText = 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyiniz.';
            if (errorData?.message) {
                errorText = Array.isArray(errorData.message)
                    ? errorData.message.join('\n')
                    : errorData.message;
            }

            dispatch(showMessage({
                title: "Hata",
                text: errorText,
                btnPrimaryText: "Tamam",
                btnPrimaryCallback: () => dispatch(hideMessage())
            }));
        } finally {
            dispatch(decrementLoadingCount());
            setIsSubmitting(false);
        }
    };

    // --- Navigate back ---

    const handleGoBack = () => {
        if (offerId) {
            history.push('/identity-verification', { offerId });
        } else {
            history.push('/identity-verification');
        }
    };

    const handleChangeIdentity = () => {
        if (offerId) {
            history.push('/identity-verification', { offerId });
        } else {
            history.push('/identity-verification');
        }
    };

    if (!offerId || !identityNumber || !phoneNumber) {
        return null;
    }

    return (
        <div className={styles.pageWrapper}>
            <button onClick={handleGoBack} className={styles.backButton}>
                <img src={backArrow} alt="Geri" />
            </button>

            <div className={styles.contentBlock}>
                <div className={styles.header}>
                    <h1>Kredi Başvurusu</h1>
                    <p>Başvuruya devam etmek için Yemeksepeti’nde kayıtlı telefon numaranızın eksik hanelerini girin.</p>
                </div>

                <div className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>VKN / TCKN</span>
                    <div className={styles.readOnlyField}>
                        <Lock size={16} className={styles.lockIcon} />
                        <span>{identityNumber}</span>
                    </div>
                </div>

                <div className={styles.phoneFieldGroup}>
                    <span className={styles.phoneLabel}>
                        Yemeksepeti’nde kayıtlı telefon numarası
                    </span>
                    <div className={styles.phoneInputContainer}>
                        {/* Numara maskeli haliyle bütün olarak gösteriliyor */}
                        <span className={styles.phoneNumberDisplay}>{displayPhoneNumber}</span>

                        <div className={styles.digitBoxes}>
                            {digits.map((digit, idx) => (
                                <input
                                    key={idx}
                                    ref={(el) => {
                                        inputRefs.current[idx] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(e, idx)}
                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                    onPaste={idx === 0 ? handlePaste : undefined}
                                    onBeforeInput={handleBeforeInput}
                                    className={
                                        error ? styles.digitInputError : styles.digitInput
                                    }
                                    aria-label={`Telefon numarası ${idx + 1}. hane`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        <AlertCircle size={14} />
                        <span>{error}</span>
                    </div>
                )}

                {!error && (
                    <div className={styles.infoNote}>
                        <Info size={14} />
                        <span>Lütfen numaranızın ortasında yer olan 5 haneyi giriniz.</span>
                    </div>
                )}

                <button
                    type="button"
                    onClick={handleChangeIdentity}
                    className={styles.changeLink}
                >
                    <Pencil size={14} />
                    VKN/TCKN'yi Değiştir
                </button>

                <hr className={styles.divider} />

                <div className={styles.footer}>
                    <img src={logo} alt="Yemeksepeti" className={styles.logo} />
                    <Button
                        type="button"
                        variant="primary"
                        className={styles.submitBtn}
                        disabled={!allDigitsFilled || isSubmitting}
                        onClick={handleSubmit}
                    >
                        Kontrol Et ve Devam Et
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PhoneVerificationPage;
